import React, { useEffect, useState, useRef } from "react";
import { MessageCircle, Mail, X } from "lucide-react";

/**
 * Floating WhatsApp button — fixed bottom-right of every page.
 *
 * Reads `/api/site-settings` once on mount. If `whatsapp_enabled` is false
 * (or there is no number yet) the button doesn't render at all.
 *
 * Smart open/closed logic:
 *  - During configured business hours (admin-set), click opens a wa.me
 *    deep-link with a research-compliance-safe pre-filled message.
 *  - Outside business hours, click opens a mailto: to the offline-fallback
 *    email and a small chip-tooltip on the button announces "Currently
 *    offline — email us instead".
 *
 * All hours are interpreted in the user's browser local time. Business hours
 * are stored as GMT/UTC integers — for a UK supplier this is the right
 * default; if we ever go international we can refine to per-region timezones.
 */
export default function WhatsAppButton() {
  const [cfg, setCfg] = useState(null);
  const [now, setNow] = useState(() => new Date());
  const [hovered, setHovered] = useState(false);
  const [popoverShown, setPopoverShown] = useState(false);
  const popoverTimer = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${BACKEND}/api/site-settings`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setCfg(d || null))
      .catch(() => setCfg(null));
  }, [BACKEND]);

  // Tick "now" every minute so the open/closed banner stays accurate.
  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(i);
  }, []);

  // First-visit popover — once per session, shows after 8s.
  useEffect(() => {
    if (!cfg?.whatsapp_enabled || !cfg?.whatsapp_number) return;
    if (sessionStorage.getItem("pg_wa_popover_seen")) return;
    popoverTimer.current = setTimeout(() => {
      setPopoverShown(true);
      sessionStorage.setItem("pg_wa_popover_seen", "1");
    }, 8000);
    return () => clearTimeout(popoverTimer.current);
  }, [cfg]);

  if (!cfg?.whatsapp_enabled || !cfg?.whatsapp_number) return null;

  const isOpenForBusiness = (() => {
    // weekday: JS getDay returns 0=Sun..6=Sat. Convert to 0=Mon..6=Sun.
    const jsDay = now.getDay();
    const day = (jsDay + 6) % 7;
    const hour = now.getHours();
    return (
      (cfg.whatsapp_days || [0, 1, 2, 3, 4]).includes(day) &&
      hour >= (cfg.whatsapp_hours_start ?? 9) &&
      hour < (cfg.whatsapp_hours_end ?? 18)
    );
  })();

  const waHref = `https://wa.me/${cfg.whatsapp_number}?text=${encodeURIComponent(cfg.whatsapp_prefilled_message || "")}`;
  const mailtoHref = `mailto:${cfg.whatsapp_offline_email || "info@peptigenlabs.co.uk"}?subject=${encodeURIComponent("Question for Peptigen Labs")}&body=${encodeURIComponent("Hi Peptigen Labs,\n\nI have a question:\n\n\n— Sent from your website (outside live-chat hours).")}`;

  const href = isOpenForBusiness ? waHref : mailtoHref;
  const label = isOpenForBusiness ? "Chat on WhatsApp" : "Currently offline — email us";

  return (
    <>
      {popoverShown && (
        <div
          role="dialog"
          aria-label="Chat with us"
          className="fixed bottom-24 right-5 sm:right-6 z-[60] w-[260px] rounded-sm border border-[#25D366]/40 bg-[#0F172A]/95 backdrop-blur-md p-3.5 shadow-[0_18px_50px_-12px_rgba(0,0,0,0.7)] animate-[pgFadeIn_220ms_ease-out_forwards]"
          data-testid="whatsapp-popover"
        >
          <button
            type="button"
            onClick={() => setPopoverShown(false)}
            className="absolute top-1.5 right-1.5 text-[#9CA8B8] hover:text-[#F7FAFC]"
            aria-label="Close"
            data-testid="whatsapp-popover-close"
          >
            <X size={13} />
          </button>
          <div className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#25D366] mb-1">
            // Got a question?
          </div>
          <div className="text-[#F7FAFC] text-[13px] leading-snug mb-2">
            {isOpenForBusiness
              ? "We're live now — ask us anything about your order, batch COAs or research products."
              : "We're offline at the moment — drop us an email and we'll reply ASAP."}
          </div>
          <a
            href={href}
            target={isOpenForBusiness ? "_blank" : "_self"}
            rel={isOpenForBusiness ? "noreferrer" : undefined}
            className="inline-flex items-center gap-1.5 text-[#A6FF00] text-xs hover:underline"
            data-testid="whatsapp-popover-cta"
          >
            {isOpenForBusiness ? "Open chat →" : "Email us →"}
          </a>
        </div>
      )}

      <a
        href={href}
        target={isOpenForBusiness ? "_blank" : "_self"}
        rel={isOpenForBusiness ? "noreferrer" : undefined}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-label={label}
        title={label}
        className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[55] group"
        data-testid="whatsapp-floating-btn"
      >
        {/* Pulse halo — only while open */}
        {isOpenForBusiness && (
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full bg-[#25D366] opacity-50 animate-ping"
            style={{ animationDuration: "2.4s" }}
          />
        )}
        <span
          className={[
            "relative flex items-center justify-center rounded-full shadow-[0_12px_40px_-8px_rgba(0,0,0,0.7)] transition-all",
            "w-14 h-14",
            isOpenForBusiness
              ? "bg-[#25D366] hover:bg-[#1EBE5C]"
              : "bg-[#0F172A] border border-[#FF8A5B]/55 hover:bg-[#1A2236]",
          ].join(" ")}
        >
          {isOpenForBusiness ? (
            <MessageCircle size={26} className="text-white" strokeWidth={2.3} />
          ) : (
            <Mail size={22} className="text-[#FF8A5B]" strokeWidth={2.3} />
          )}
        </span>

        {/* Hover tooltip chip */}
        {hovered && (
          <span
            className={[
              "absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap",
              "px-3 py-1.5 rounded-sm text-[11px] pg-mono uppercase tracking-[0.18em] border",
              isOpenForBusiness
                ? "bg-[#0F172A] border-[#25D366]/45 text-[#25D366]"
                : "bg-[#0F172A] border-[#FF8A5B]/45 text-[#FF8A5B]",
            ].join(" ")}
          >
            {isOpenForBusiness ? "Chat with us" : "We're offline — email us"}
          </span>
        )}
      </a>

      <style>{`
        @keyframes pgFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
